import React, { useState, useEffect } from 'react';
import { orgChartAPI, hrAPI } from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Network, Users, Building2, ChevronDown, ChevronRight } from 'lucide-react';

export default function AdminOrgChart() {
  const [nodes, setNodes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('tree');
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [orgRes, deptRes] = await Promise.all([
        orgChartAPI.get(),
        hrAPI.departments(),
      ]);
      setNodes(orgRes.data.nodes || []);
      setDepartments(deptRes.data.departments || []);
    } catch { toast.error('Failed to load org chart'); }
    finally { setLoading(false); }
  };

  const toggleNode = (id) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const getChildren = (managerId) => nodes.filter(n => n.manager_id === managerId);
  const getRootNodes = () => nodes.filter(n => !n.manager_id);
  const getDeptName = (id) => departments.find(d => d.id === id)?.name || '';

  const ROLE_COLORS = {
    super_admin: '#7C3AED', admin: '#B3261E', editor: '#1E5A8A',
    producer: '#0891B2', journalist: '#B89B5E', photographer: '#059669',
    designer: '#DB2777', data_journalist: '#7C3AED', social_media_manager: '#0284C7',
    seo_specialist: '#EA580C', ad_operations: '#CA8A04', subscription_manager: '#0D9488',
    analytics_analyst: '#4F46E5', hr_admin: '#65A30D', finance_controller: '#D97706',
    legal_counsel: '#E11D48', external_contributor: '#6B7280', moderator: '#9A6B1A',
    reader: '#5B616E',
    editor_in_chief: '#4A1D2A', section_editor: '#1E5A8A', sub_editor: '#1F7A5A',
  };

  const renderNode = (node, depth = 0) => {
    const children = getChildren(node.id);
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = children.length > 0;
    
    return (
      <div key={node.id} style={{ marginLeft: depth * 32 }}>
        <div className={`flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-[#F5F0E8] cursor-pointer transition-colors ${depth === 0 ? 'bg-white border border-[#E5DFD5] mb-2' : ''}`} onClick={() => hasChildren && toggleNode(node.id)}>
          {hasChildren ? (
            isExpanded ? <ChevronDown className="w-4 h-4 text-[#5B616E]" /> : <ChevronRight className="w-4 h-4 text-[#5B616E]" />
          ) : <div className="w-4 h-4" />}
          {node.avatar_url ? (
            <img src={node.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: ROLE_COLORS[node.role] || '#5B616E' }}>
              {node.full_name?.charAt(0) || '?'}
            </div>
          )}
          <div className="flex-1">
            <p className="text-sm font-medium text-[#1A1A1A]">{node.full_name}</p>
            <div className="flex items-center gap-2">
              <Badge className="text-[10px] px-1.5" style={{ backgroundColor: (ROLE_COLORS[node.role] || '#5B616E') + '20', color: ROLE_COLORS[node.role] || '#5B616E' }}>
                {node.role?.replace(/_/g, ' ')}
              </Badge>
              {node.department_id && <span className="text-[10px] text-[#5B616E]">{getDeptName(node.department_id)}</span>}
            </div>
          </div>
          {hasChildren && <span className="text-[10px] text-[#5B616E]">{children.length} reports</span>}
        </div>
        {hasChildren && isExpanded && (
          <div className="border-l-2 border-[#E5DFD5] ml-5">
            {children.map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const groupedByDept = () => {
    const groups = {};
    departments.forEach(d => { groups[d.id] = { dept: d, members: [] }; });
    groups['unassigned'] = { dept: { name: 'Unassigned', id: 'unassigned' }, members: [] };
    nodes.forEach(n => {
      const deptId = n.department_id || 'unassigned';
      if (!groups[deptId]) groups[deptId] = { dept: { name: 'Unknown', id: deptId }, members: [] };
      groups[deptId].members.push(n);
    });
    return Object.values(groups).filter(g => g.members.length > 0);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Organization Chart</h1>
          <p className="text-sm text-[#5B616E] mt-1">{nodes.length} staff members across {departments.length} departments</p>
        </div>
        <div className="flex gap-1 bg-[#F5F0E8] rounded-lg p-1">
          <button onClick={() => setViewMode('tree')} className={`px-3 py-1.5 rounded-md text-sm ${viewMode === 'tree' ? 'bg-white shadow-sm' : 'text-[#5B616E]'}`}>
            <Network className="w-4 h-4 inline mr-1" /> Tree
          </button>
          <button onClick={() => setViewMode('dept')} className={`px-3 py-1.5 rounded-md text-sm ${viewMode === 'dept' ? 'bg-white shadow-sm' : 'text-[#5B616E]'}`}>
            <Building2 className="w-4 h-4 inline mr-1" /> By Department
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-[#5B616E]">Loading organization chart...</div>
      ) : viewMode === 'tree' ? (
        <div className="space-y-1">
          {getRootNodes().length > 0 ? getRootNodes().map(node => renderNode(node)) : nodes.map(node => renderNode(node))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {groupedByDept().map(group => (
            <div key={group.dept.id} className="bg-white rounded-xl border border-[#E5DFD5] overflow-hidden">
              <div className="bg-[#F5F0E8] px-5 py-3 border-b border-[#E5DFD5] flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[#1E5A8A]" />
                <span className="font-medium text-sm">{group.dept.name}</span>
                <Badge variant="outline" className="ml-auto text-[10px]">{group.members.length} members</Badge>
              </div>
              <div className="divide-y divide-[#E5DFD5]">
                {group.members.map(m => (
                  <div key={m.id} className="px-5 py-3 flex items-center gap-3">
                    {m.avatar_url ? (
                      <img src={m.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: ROLE_COLORS[m.role] || '#5B616E' }}>
                        {m.full_name?.charAt(0)}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium">{m.full_name}</p>
                      <p className="text-[10px] text-[#5B616E] capitalize">{m.role?.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
